import React from "react";
import { ChevronDown, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";
import { Button } from "../../@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../../@/components/ui/dropdown-menu";

export default function Pagination({
  currentPage,
  totalPages,
  rowsPerPage,
  rowsPerPageOptions = [10, 25, 50],
  onRowsPerPageChange,
  onPageChange,
  totalRows,
  selectedCount = 0,
  rowLabel = "rows items",
  showSelectedCount = true,
}) {
  const handlePrevious = () => onPageChange(Math.max(currentPage - 1, 1));
  const handleNext = () => onPageChange(Math.min(currentPage + 1, totalPages));

  return (
    <div className="px-4 py-1 mt-1">
      {/* Mobile: only page count + single chevrons */}
      <div className="flex sm:hidden items-center justify-between gap-2">
        <span className="text-sm text-(--neutral-700)">
          Page {currentPage} of {totalPages}
        </span>
        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size=""
            onClick={handlePrevious}
            disabled={currentPage === 1}
            className="p-1 border-(--neutral-300) bg-(--surface-panel) text-(--neutral-800) disabled:opacity-50 disabled:cursor-not-allowed hover:bg-(--neutral-100) hover:border-(--color-accent)"
            aria-label="Previous page"
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button
            variant="outline"
            size=""
            onClick={handleNext}
            disabled={currentPage === totalPages}
            className="p-1 border-(--neutral-300) bg-(--surface-panel) text-(--neutral-800) disabled:opacity-50 disabled:cursor-not-allowed hover:bg-(--neutral-100) hover:border-(--color-accent)"
            aria-label="Next page"
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Desktop (sm+): keep full controls */}
      <div className="hidden sm:flex items-center justify-between gap-3">
        <div className="flex items-center text-sm font-semibold text-(--neutral-700)">
          <span>{totalRows} {rowLabel}</span>
          {showSelectedCount && selectedCount > 0 && (
            <span className="ml-3 text-sm font-semibold text-(--neutral-700)">• {selectedCount} selected</span>
          )}
        </div>

        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2 text-sm text-(--neutral-600)">
            <span className="text-sm text-(--neutral-700) font-semibold">Rows per page</span>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="" className="w-16 justify-between text-(--neutral-900) border-(--neutral-300) bg-(--surface-panel) hover:bg-(--neutral-100)">
                  {rowsPerPage}
                  <ChevronDown className="ml-1 h-3 w-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                {rowsPerPageOptions.map((option) => (
                  <DropdownMenuItem
                    key={option}
                    onClick={() => {
                      onRowsPerPageChange(option);
                    }}
                  >
                    {option}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <span className="text-sm text-(--neutral-700) font-semibold">
            Page {currentPage} of {totalPages}
          </span>

          <div className="flex items-center gap-1">
            {[
              { icon: ChevronsLeft, onClick: () => onPageChange(1), disabled: currentPage === 1 },
              { icon: ChevronLeft, onClick: handlePrevious, disabled: currentPage === 1 },
              { icon: ChevronRight, onClick: handleNext, disabled: currentPage === totalPages },
              { icon: ChevronsRight, onClick: () => onPageChange(totalPages), disabled: currentPage === totalPages },
            ].map(({ icon: Icon, onClick, disabled }, idx) => (
              <Button
                key={idx}
                variant="outline"
                size=""
                onClick={onClick}
                disabled={disabled}
                className="p-1 border-(--neutral-300) bg-(--surface-panel) text-(--neutral-800) disabled:opacity-50 disabled:cursor-not-allowed hover:bg-(--neutral-100) hover:border-(--color-accent)"
              >
                <Icon className="w-4 h-4" />
              </Button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}